import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { graphql, readInlineData } from "react-relay";

import { EntityLink } from "scmp-app/components/entity-link";
import { getResponsiveProps } from "scmp-app/components/header/features/helpers";
import type { HeaderFeatureCommonProps } from "scmp-app/components/header/features/types";
import { currentArticleAtom } from "scmp-app/lib/current-item";
import type { entityLinkHeaderFeaturesArticle$key } from "scmp-app/queries/__generated__/entityLinkHeaderFeaturesArticle.graphql";

import { Container } from "./styles";

export type entityLinkNameSize = "large" | "small";

export type HeaderFeaturesEntityLinkProps = HeaderFeatureCommonProps & {
  isMagazineStyle?: boolean;
  size?: entityLinkNameSize;
  variant?: "center" | "left";
};

export const HeaderFeaturesEntityLink: FunctionComponent<HeaderFeaturesEntityLinkProps> = ({
  isMagazineStyle = false,
  size = "small",
  variant = "left",
  ...props
}) => {
  const currentArticle = useAtomValue(currentArticleAtom);
  if (!currentArticle) return null;

  const data = readInlineData<entityLinkHeaderFeaturesArticle$key>(
    graphql`
      fragment entityLinkHeaderFeaturesArticle on Article @inline {
        authors {
          name
          ...entityLink
        }
        sections {
          value {
            name
            published
            ...entityLink
          }
        }
      }
    `,
    currentArticle,
  );

  const sectionPath = data?.sections?.[0]?.value;
  if (!sectionPath || sectionPath.length === 0) return null;

  const rootSection = sectionPath[0];
  const targetSection = sectionPath.toReversed().find(section => section?.published === true);

  if (!targetSection || !rootSection) return null;

  const rootName = rootSection.name;
  const targetName = targetSection.name;

  const editorialAuthor = data.authors?.find(
    author => author?.name?.trim().toLowerCase() === "scmp editorial",
  );
  const isSCMPEditorial = !!editorialAuthor;
  const isOpinionRoot = rootName === "Opinion";

  let displayReference: typeof editorialAuthor | typeof targetSection = targetSection;
  let displayName = targetName ?? null;

  if (isOpinionRoot) {
    displayReference = rootSection;
    displayName = rootName ?? null;

    if (variant === "center" && isSCMPEditorial) {
      displayName = "Editorial";
      displayReference = editorialAuthor;
    }
  }

  return (
    <Container $isMagazineStyle={isMagazineStyle} $size={size} {...getResponsiveProps(props)}>
      <EntityLink reference={displayReference}>{displayName}</EntityLink>
    </Container>
  );
};

HeaderFeaturesEntityLink.displayName = "HeaderFeaturesEntityLink";
